import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  AccountType,
  CardNetwork,
  ThaiBank,
  detectBank,
  detectCardNetwork,
} from "@boo/shared";
import type { AccountItem, BankConfigAccountHint } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError } from "@/services/expenseApi";
import { fetchBankConfigHints } from "@/services/accountApi";
import { useAccountStore } from "../store/useAccountStore";
import { ACCOUNT_TYPE_LABELS, INPUT_CLASS } from "../types/accounts.types";
import {
  BANK_OPTIONS,
  NETWORK_OPTIONS,
  bankLogoInfo,
  networkInfo,
} from "../lib/bankLogos";
import AccountAvatar from "./AccountAvatar";

const ACCOUNT_TYPES = Object.values(AccountType);
// Select value meaning "detect from the name and institution".
const AUTO = "AUTO";

interface Props {
  /** When set, the form edits this account instead of creating one. */
  account?: AccountItem;
  onDone?: () => void;
}

export default function AccountForm({ account, onDone }: Props) {
  const { createAccount, updateAccount } = useAccountStore();
  const [name, setName] = useState(account?.name ?? "");
  const [type, setType] = useState<AccountType>(
    account?.type ?? AccountType.Bank,
  );
  const [institution, setInstitution] = useState(account?.institution ?? "");
  const [digits, setDigits] = useState(account?.digits ?? "");
  const [bankChoice, setBankChoice] = useState<string>(
    account?.bankCode ?? AUTO,
  );
  const [networkChoice, setNetworkChoice] = useState<string>(
    account?.cardNetwork ?? AUTO,
  );
  const [saving, setSaving] = useState(false);
  const [hints, setHints] = useState<BankConfigAccountHint[]>([]);

  // Offer account numbers already saved on bank configs as pre-fills.
  useEffect(() => {
    if (account) return;
    async function loadHints() {
      try {
        setHints(await fetchBankConfigHints());
      } catch {
        // Hints are optional; the form works without them.
      }
    }
    void loadHints();
  }, [account]);

  function applyHint(hint: BankConfigAccountHint) {
    setName(hint.label);
    setInstitution(hint.label);
    setDigits(hint.digits);
  }

  const digitsValid = digits === "" || /^\d{4}$/.test(digits);

  const isCard = type === AccountType.CreditCard;
  const detectedBank = detectBank(name, institution);
  const detectedNetwork = isCard
    ? detectCardNetwork([name, institution])
    : null;
  const previewBank =
    bankChoice === AUTO ? detectedBank : (bankChoice as ThaiBank);
  const previewNetwork =
    networkChoice === AUTO ? detectedNetwork : (networkChoice as CardNetwork);

  async function handleSubmit(e: { preventDefault(): void }) {
    e.preventDefault();
    if (!name.trim() || !digitsValid) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        type,
        institution: institution.trim() || undefined,
        digits: digits || undefined,
        // Auto: omit on create, null on edit so the server re-detects.
        bankCode:
          bankChoice === AUTO
            ? account
              ? null
              : undefined
            : (bankChoice as ThaiBank),
        cardNetwork:
          !isCard || networkChoice === AUTO
            ? account
              ? null
              : undefined
            : (networkChoice as CardNetwork),
      };
      if (account) {
        await updateAccount(account.id, payload);
      } else {
        await createAccount(payload);
        setName("");
        setInstitution("");
        setDigits("");
        setBankChoice(AUTO);
        setNetworkChoice(AUTO);
      }
      onDone?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const form = (
    <form onSubmit={handleSubmit} className="grid gap-2 sm:grid-cols-2">
      {hints.length > 0 && (
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          From your bank settings:
          {hints.map((hint) => (
            <button
              key={`${hint.label}-${hint.digits}`}
              type="button"
              onClick={() => applyHint(hint)}
              className="rounded-full border border-border px-2.5 py-1 text-foreground hover:bg-muted [font-variant-numeric:tabular-nums]"
            >
              {hint.label} ••{hint.digits}
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        placeholder="Name (e.g. KBank savings)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
        className={`${INPUT_CLASS} py-2`}
      />
      <select
        value={type}
        onChange={(e) => setType(e.target.value as AccountType)}
        className={`${INPUT_CLASS} py-2`}
      >
        {ACCOUNT_TYPES.map((t) => (
          <option key={t} value={t}>
            {ACCOUNT_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Institution (optional)"
        value={institution}
        onChange={(e) => setInstitution(e.target.value)}
        maxLength={100}
        className={`${INPUT_CLASS} py-2`}
      />
      <input
        type="text"
        inputMode="numeric"
        placeholder="Last 4 digits (optional)"
        value={digits}
        onChange={(e) =>
          setDigits(e.target.value.replace(/\D/g, "").slice(0, 4))
        }
        className={`${INPUT_CLASS} py-2 [font-variant-numeric:tabular-nums]`}
      />
      <div className="sm:col-span-2 flex items-center gap-3">
        <AccountAvatar
          type={type}
          bankCode={previewBank}
          cardNetwork={previewNetwork}
        />
        <div className="grid flex-1 min-w-0 gap-2 sm:grid-cols-2">
          <select
            value={bankChoice}
            onChange={(e) => setBankChoice(e.target.value)}
            aria-label="Bank logo"
            className={`${INPUT_CLASS} py-2`}
          >
            <option value={AUTO}>
              {`Logo: auto-detect${bankLogoInfo(detectedBank) ? ` (${bankLogoInfo(detectedBank)?.label})` : ""}`}
            </option>
            {BANK_OPTIONS.map((bank) => (
              <option key={bank.code} value={bank.code}>
                {bank.label}
              </option>
            ))}
            <option value={ThaiBank.None}>No logo</option>
          </select>
          {isCard && (
            <select
              value={networkChoice}
              onChange={(e) => setNetworkChoice(e.target.value)}
              aria-label="Card network"
              className={`${INPUT_CLASS} py-2`}
            >
              <option value={AUTO}>
                {`Network: auto-detect${networkInfo(detectedNetwork) ? ` (${networkInfo(detectedNetwork)?.label})` : ""}`}
              </option>
              {NETWORK_OPTIONS.map((network) => (
                <option key={network.code} value={network.code}>
                  {network.label}
                </option>
              ))}
              <option value={CardNetwork.None}>No network</option>
            </select>
          )}
        </div>
      </div>
      {type === AccountType.Cash && !account && (
        <p className="sm:col-span-2 text-xs text-amber-600">
          Cash spending isn't imported — log it by hand to keep this balance
          accurate.
        </p>
      )}
      <div className="sm:col-span-2 flex justify-end gap-2">
        {account && (
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={!name.trim() || !digitsValid}
          loading={saving}
        >
          {!account && !saving && <Plus className="w-4 h-4 mr-1" />}
          {account ? "Save" : "Add account"}
        </Button>
      </div>
    </form>
  );

  if (account) return form;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">New account</CardTitle>
      </CardHeader>
      <CardContent>{form}</CardContent>
    </Card>
  );
}
